/**
 * DecisionTrailPanel — the "Why is this #N?" inspector (canon 27 §5, the Trail).
 *
 * Opens from any ranked row (Published Priority List, Focus List, Plan of
 * Record lane) and assembles the item's decision trail from durable sources:
 * memorialized decisions, governance events with rationale, gated-reorder
 * displacement, list publication, value proposals/acceptances, rescores, and
 * item lineage — plus session ranking facts (rank, score, criteria) from the
 * session engine's output.
 *
 * Honesty contract: every fact rendered comes from the store. A moment whose
 * rationale was never recorded says so explicitly — the panel never invents
 * a narrative. Empty sections state what has not been memorialized.
 *
 * The panel is URL-addressable: host surfaces sync the open item through the
 * `?why=<itemId>` search param (useDecisionTrailParam), so the answer to
 * "why is this number four?" is a URL.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Copy,
  ExternalLink,
  GitBranch,
  Landmark,
  ListOrdered,
  Loader2,
  SlidersHorizontal,
  Scale,
  Shield,
} from 'lucide-react';
import { SlideOverPanelStandalone } from '../panels/SlideOverPanel';
import {
  fetchDecisionTrail,
  fetchCriterionScores,
  deriveSessionRankingFacts,
  type CriterionScoreSource,
  type DecisionTrailEntry,
  type SessionCriterionScores,
  type SessionRankingFact,
} from '../../lib/decisionTrailService';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useAttributesStore } from '../../stores/prioritization/attributesStore';
import { useDecisionsStore } from '../../stores/prioritization/decisionsStore';
import { useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap';
import { fetchItemRankings } from '../../lib/itemRankingsService';
import { rankStandingLabel, type ItemRanking } from '../../lib/itemRankings';
import { decisionForRanking, pickRankingForTrail, rankingDecisionGapText } from '../../lib/call/rankingDecision';
import { fetchListApprovalDecisionIds } from '../../lib/call/rankingDecisionService';
import { CallSentence } from './decisions/CallSentence';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatMoment(iso: string | null): string {
  if (!iso) return 'Undated';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

const KIND_BADGES: Record<DecisionTrailEntry['kind'], { label: string; className: string }> = {
  decision:          { label: 'Decision',     className: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  plan_commitment:   { label: 'Plan',         className: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
  governance_event:  { label: 'Governance',   className: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
  rank_displacement: { label: 'Reorder',      className: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  list_published:    { label: 'Published',    className: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
  value_proposed:    { label: 'Proposed',     className: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  value_accepted:    { label: 'Accepted',     className: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
  rescore:           { label: 'Rescore',      className: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  item_lineage:      { label: 'Identity',     className: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
  recorded_rationale:{ label: 'Rationale',    className: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
};

function SectionHeading({ icon: Icon, children }: { icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <h3 className="flex items-center gap-1.5 text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-2">
      <Icon className="h-3.5 w-3.5 text-[var(--text-ter)]" />
      {children}
    </h3>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-[var(--text-ter)] italic">{children}</p>;
}

// ── Trail entry row ───────────────────────────────────────────────────────────

function TrailEntryRow({
  entry,
  actorDisplayMap,
}: {
  entry: DecisionTrailEntry;
  actorDisplayMap: Record<string, string>;
}) {
  const badge = KIND_BADGES[entry.kind];
  const actor =
    entry.actorName ??
    (entry.actorId ? actorDisplayMap[entry.actorId] ?? entry.actorId : null);

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2.5">
      <div className="flex items-start gap-2">
        <span className={`mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0 ${badge.className}`}>
          {badge.label}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--text-pri)]">{entry.title}</p>
          <p className="mt-0.5 text-xs text-[var(--text-ter)]">
            {formatMoment(entry.at)}
            {actor ? <> · by {actor}</> : <> · <span className="italic">no actor recorded</span></>}
          </p>
          {entry.why ? (
            <p className="mt-1.5 text-sm text-[var(--text-sec)] leading-relaxed whitespace-pre-wrap">{entry.why}</p>
          ) : (
            <p className="mt-1.5 text-xs text-[var(--text-ter)] italic">No rationale recorded for this moment.</p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            {entry.decisionId && (
              <Link
                to={`/prioritization/decisions?view=map&selected=${entry.decisionId}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 hover:underline"
              >
                View decision record
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
            {entry.sessionId && (
              <Link
                to={`/prioritization/sessions/${entry.sessionId}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 hover:underline"
              >
                View session
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Session ranking facts ─────────────────────────────────────────────────────

function SessionFactCard({
  fact,
  criterionLabel,
  actorDisplayMap,
}: {
  fact: SessionRankingFact;
  criterionLabel: (key: string) => string;
  actorDisplayMap: Record<string, string>;
}) {
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <Link
          to={`/prioritization/sessions/${fact.sessionId}`}
          className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
        >
          {fact.sessionName}
        </Link>
        <span className="flex-shrink-0 rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs font-semibold text-[var(--text-sec)] tabular-nums">
          #{fact.rank} of {fact.of}
        </span>
      </div>
      <p className="mt-0.5 text-xs text-[var(--text-ter)]">
        {fact.state ? `${fact.state.replace(/_/g, ' ').toLowerCase()} session` : 'session'}
        {fact.toolName ? ` · ${fact.toolName}` : ''}
        {fact.totalScore != null ? ` · composite score ${fact.totalScore}` : ''}
      </p>
      {fact.scoresByCriterion.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {fact.scoresByCriterion.map((s) => (
            <span
              key={s.key}
              className="inline-flex items-center gap-1 border border-[var(--rule)] bg-[var(--surface)] px-1.5 py-0.5 text-xs text-[var(--text-sec)]"
              title={s.weight != null ? `Weight ${s.weight}` : undefined}
            >
              <span className="font-medium text-[var(--text-sec)]">{criterionLabel(s.key)}</span>
              <span className="tabular-nums">{s.score}</span>
              {s.weight != null && <span className="text-[var(--text-ter)]">w {s.weight}</span>}
            </span>
          ))}
        </div>
      )}
      {fact.overrides.map((o, i) => (
        <div key={i} className="mt-2 border border-[var(--mustard)] bg-[var(--mustard-dim)] px-2.5 py-2">
          <p className="text-xs font-medium text-[var(--mustard)] flex items-center gap-1">
            Rank override: #{o.fromRank} <ArrowRight className="h-3 w-3" /> #{o.toRank}
            <span className="font-normal text-[var(--mustard)]">
              · by {actorDisplayMap[o.overriddenBy] ?? o.overriddenBy}
            </span>
          </p>
          <p className="mt-0.5 text-sm text-[var(--mustard)] leading-relaxed">{o.rationale}</p>
        </div>
      ))}
    </div>
  );
}

// ── Scores by criterion ───────────────────────────────────────────────────────

function formatScore(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function scorerLine(source: CriterionScoreSource, count: number): string {
  const people = `${count} ${count === 1 ? 'person' : 'people'}`;
  switch (source) {
    case 'group':
      return `Scored together · ${people}`;
    case 'participants':
      return `Scored one by one · ${people}`;
    case 'facilitator':
      return 'Entered by the facilitator';
  }
}

function CriterionScoresCard({ session }: { session: SessionCriterionScores }) {
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2.5">
      <Link
        to={`/prioritization/sessions/${session.sessionId}`}
        className="text-sm font-medium text-[var(--text-pri)] hover:text-primary-700 hover:underline truncate"
      >
        {session.sessionName}
      </Link>
      {session.sources.map((bucket) => (
        <div key={bucket.source} className="mt-2">
          <p className="text-xs text-[var(--text-ter)]">{scorerLine(bucket.source, bucket.scorerCount)}</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {bucket.scores.map((score) => (
              <span
                key={score.key}
                className="inline-flex items-center gap-1 border border-[var(--rule)] bg-[var(--surface)] px-1.5 py-0.5 text-xs text-[var(--text-sec)]"
              >
                <span className="font-medium">{score.label}</span>
                <span className="tabular-nums">{formatScore(score.mean)}</span>
                {score.min !== score.max && (
                  <span className="tabular-nums text-[var(--text-ter)]">
                    ({formatScore(score.min)}–{formatScore(score.max)})
                  </span>
                )}
                {score.weight != null && <span className="text-[var(--text-ter)]">w {formatScore(score.weight)}</span>}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export interface DecisionTrailPanelProps {
  open: boolean;
  onClose: () => void;
  itemId: string | null;
  /** The rank the user clicked (e.g. 4 → header asks "Why is this #4?"). */
  rankLabel?: number | string | null;
  /** The ranked surface the user came from (e.g. list or plan name). */
  contextLabel?: string | null;
}

export function DecisionTrailPanel({
  open,
  onClose,
  itemId,
  rankLabel,
  contextLabel,
}: DecisionTrailPanelProps) {
  const [entries, setEntries] = useState<DecisionTrailEntry[]>([]);
  const [criterionScores, setCriterionScores] = useState<SessionCriterionScores[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // The call behind the rank the person clicked, drawn first (owner,
  // 2026-10-30: a standing is never drawn alone). Read from the item's
  // rankings and the workspace's decision record; the list→decision link
  // lives on the approval's governance event.
  const [rankings, setRankings] = useState<ItemRanking[] | null>(null);
  const [listDecisionIds, setListDecisionIds] = useState<ReadonlyMap<string, string> | null>(null);
  const decisions = useDecisionsStore((s) => s.decisions);
  const fetchDecisions = useDecisionsStore((s) => s.fetchDecisions);
  const [decisionsReady, setDecisionsReady] = useState(decisions.length > 0);

  const sessions = useSessionStore((s) => s.sessions);
  const items = useItemsStore((s) => s.items);
  const getAttributeByKey = useAttributesStore((s) => s.getAttributeByKey);

  const item = useMemo(
    () => (itemId ? items.find((i) => i.id === itemId) ?? null : null),
    [items, itemId]
  );

  useEffect(() => {
    if (!open || !itemId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEntries([]);
    setCriterionScores([]);
    void Promise.all([fetchDecisionTrail(itemId), fetchCriterionScores(itemId)]).then(([result, scores]) => {
      if (cancelled) return;
      setEntries(result.entries);
      setCriterionScores(scores.sessions);
      setError(result.error ?? scores.error);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, itemId]);

  useEffect(() => {
    if (!open || !itemId) return;
    let cancelled = false;
    setRankings(null);
    setListDecisionIds(null);
    void fetchItemRankings(itemId)
      .then(async (r) => {
        if (cancelled) return;
        setRankings(r.rankings);
        const setIds = r.rankings
          .filter((x) => x.standing === 'agreed' && x.prioritySetId)
          .map((x) => x.prioritySetId as string);
        const ids = setIds.length ? await fetchListApprovalDecisionIds(setIds).catch(() => new Map<string, string>()) : new Map<string, string>();
        if (!cancelled) setListDecisionIds(ids);
      })
      .catch(() => {
        if (!cancelled) {
          setRankings([]);
          setListDecisionIds(new Map());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, itemId]);

  useEffect(() => {
    if (!open) return;
    if (decisions.length > 0) {
      setDecisionsReady(true);
      return;
    }
    let cancelled = false;
    void fetchDecisions().finally(() => {
      if (!cancelled) setDecisionsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [open, decisions.length, fetchDecisions]);

  const rankingForCall = useMemo(
    () => (rankings ? pickRankingForTrail(rankings, rankLabel, contextLabel) : null),
    [rankings, rankLabel, contextLabel]
  );
  const rankingCall = useMemo(
    () => (rankingForCall && decisionsReady && listDecisionIds ? decisionForRanking(rankingForCall, decisions, listDecisionIds) : null),
    [rankingForCall, decisionsReady, listDecisionIds, decisions]
  );

  const sessionFacts = useMemo(
    () => (itemId ? deriveSessionRankingFacts(itemId, sessions) : []),
    [itemId, sessions]
  );

  const actorIds = useMemo(
    () => [
      ...entries.map((e) => e.actorId),
      ...sessionFacts.flatMap((f) => f.overrides.map((o) => o.overriddenBy)),
    ],
    [entries, sessionFacts]
  );
  const actorDisplayMap = useActorDisplayMap(actorIds);

  const criterionLabel = useCallback(
    (key: string) => getAttributeByKey(key)?.label ?? key,
    [getAttributeByKey]
  );

  const sections = useMemo(() => {
    const byKind = (kinds: DecisionTrailEntry['kind'][]) =>
      entries.filter((e) => kinds.includes(e.kind));
    return {
      // Plan-level Decisions (a plan of record that placed this item) sit with
      // the other Decisions, marked with their own "Plan" badge.
      decisions: byKind(['decision', 'plan_commitment']),
      valuation: byKind(['value_proposed', 'value_accepted', 'rescore']),
      ordering: byKind(['rank_displacement', 'list_published']),
      governance: byKind(['governance_event', 'item_lineage']),
    };
  }, [entries]);

  const handleCopyLink = useCallback(() => {
    if (typeof window === 'undefined') return;
    void navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  const title = rankLabel != null ? `Why is this #${rankLabel}?` : 'Decision Trail';

  const footer = (
    <div className="flex items-center justify-between gap-2">
      {itemId ? (
        <div className="flex items-center gap-3">
          <Link
            to={`/prioritization/items/${itemId}/journey`}
            className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline"
          >
            Full journey
          </Link>
          <Link
            to={`/prioritization/items/${itemId}?tab=lineage`}
            className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline"
          >
            Item lineage
          </Link>
        </div>
      ) : (
        <span />
      )}
      <button
        type="button"
        onClick={onClose}
        className="bg-[var(--bg-raised)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        Close
      </button>
    </div>
  );

  return (
    <SlideOverPanelStandalone open={open} onClose={onClose} title={title} size="lg" footer={footer}>
      {!itemId ? (
        <EmptyNote>No item selected.</EmptyNote>
      ) : (
        <div className="space-y-6">
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div className="space-y-1.5">
            <div className="flex items-start justify-between gap-2">
              <h2 className="text-base font-semibold text-[var(--text-pri)] leading-snug">
                {item?.title ?? 'Item'}
              </h2>
              <button
                type="button"
                onClick={handleCopyLink}
                className="inline-flex flex-shrink-0 items-center gap-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
                title="Copy a shareable link to this trail"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-[var(--green)]" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
            <p className="text-xs text-[var(--text-ter)]">
              {contextLabel ? `${contextLabel} · ` : ''}
              Every fact below is read from the decision record — who, when, why.
            </p>
          </div>

          {/* ── The call behind this rank ──────────────────────────────── */}
          <section data-testid="trail-call" className="border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2.5">
            {rankings === null || !decisionsReady || listDecisionIds === null ? (
              <p className="text-sm text-[var(--text-ter)]">Reading the decision record…</p>
            ) : !rankingForCall ? (
              <EmptyNote>No ranking of this item is on the record, so there is no call to read.</EmptyNote>
            ) : (
              <>
                {rankingCall?.decision ? (
                  <CallSentence
                    compact
                    eyebrow="The call behind this rank"
                    decision={rankingCall.decision}
                    decisions={decisions}
                  />
                ) : (
                  <>
                    <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">The call behind this rank</p>
                    <p className="mt-1 text-sm text-[var(--text-sec)]">
                      {rankingDecisionGapText(rankingForCall, rankingCall?.gap ?? 'not_on_record')}
                    </p>
                  </>
                )}
                <p className="mt-2 text-xs text-[var(--text-ter)]">
                  {rankingForCall.rank != null ? `#${rankingForCall.rank}` : 'Not ranked'} in {rankingForCall.sourceName} · {rankStandingLabel(rankingForCall)}
                </p>
              </>
            )}
          </section>

          {error && (
            <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] px-3 py-2 text-sm text-[var(--mustard)]">
              Some trail sources could not be loaded: {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-[var(--text-ter)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Assembling the trail…</span>
            </div>
          ) : (
            <>
              {/* ── 1. Ranked in sessions ───────────────────────────────── */}
              <section>
                <SectionHeading icon={ListOrdered}>Ranked in sessions</SectionHeading>
                {sessionFacts.length > 0 ? (
                  <div className="space-y-2">
                    {sessionFacts.map((fact) => (
                      <SessionFactCard
                        key={fact.sessionId}
                        fact={fact}
                        criterionLabel={criterionLabel}
                        actorDisplayMap={actorDisplayMap}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyNote>No session ranking includes this item.</EmptyNote>
                )}
              </section>

              {/* ── 1b. Scores by criterion (group, participants, facilitator) ── */}
              <section>
                <SectionHeading icon={SlidersHorizontal}>Scored by criterion</SectionHeading>
                {criterionScores.length > 0 ? (
                  <div className="space-y-2">
                    {criterionScores.map((session) => (
                      <CriterionScoresCard key={session.sessionId} session={session} />
                    ))}
                  </div>
                ) : (
                  <EmptyNote>No scores by criterion are recorded for this item yet. Group and one-by-one scores appear once a session's scoring is over.</EmptyNote>
                )}
              </section>

              {/* ── 2. Memorialized decisions ───────────────────────────── */}
              <section>
                <SectionHeading icon={Landmark}>Memorialized decisions</SectionHeading>
                {sections.decisions.length > 0 ? (
                  <div className="space-y-2">
                    {sections.decisions.map((entry) => (
                      <TrailEntryRow key={`${entry.sourceTable}-${entry.sourceId}`} entry={entry} actorDisplayMap={actorDisplayMap} />
                    ))}
                  </div>
                ) : (
                  <EmptyNote>No memorialized decision references this item yet.</EmptyNote>
                )}
              </section>

              {/* ── 3. Valuation ────────────────────────────────────────── */}
              <section>
                <SectionHeading icon={Scale}>Valuation</SectionHeading>
                {sections.valuation.length > 0 ? (
                  <div className="space-y-2">
                    {sections.valuation.map((entry) => (
                      <TrailEntryRow key={`${entry.kind}-${entry.sourceId}`} entry={entry} actorDisplayMap={actorDisplayMap} />
                    ))}
                  </div>
                ) : (
                  <EmptyNote>No value proposals or rescores are recorded for this item.</EmptyNote>
                )}
              </section>

              {/* ── 4. Order & publication ──────────────────────────────── */}
              <section>
                <SectionHeading icon={Shield}>Order changes &amp; publication</SectionHeading>
                {sections.ordering.length > 0 ? (
                  <div className="space-y-2">
                    {sections.ordering.map((entry) => (
                      <TrailEntryRow key={`${entry.sourceTable}-${entry.sourceId}`} entry={entry} actorDisplayMap={actorDisplayMap} />
                    ))}
                  </div>
                ) : (
                  <EmptyNote>
                    No published-list events include this item — it has not entered a published
                    ranking, or the publication predates memorialization.
                  </EmptyNote>
                )}
              </section>

              {/* ── 5. Governance & identity ────────────────────────────── */}
              {sections.governance.length > 0 && (
                <section>
                  <SectionHeading icon={GitBranch}>Governance &amp; identity</SectionHeading>
                  <div className="space-y-2">
                    {sections.governance.map((entry) => (
                      <TrailEntryRow key={`${entry.sourceTable}-${entry.sourceId}`} entry={entry} actorDisplayMap={actorDisplayMap} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      )}
    </SlideOverPanelStandalone>
  );
}

export default DecisionTrailPanel;
